import { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { gradient, text } from 'chaoskit/src/assets/styles/utility'
import { rgba } from 'polished'
import useIntersection from 'react-use/lib/useIntersection'
import { motion, useAnimation } from 'framer-motion'
import { List, ListItem } from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'

import Image from '~components/Image'
import { VideoModal } from '~components/Video'
import Icon from '~components/Icon'
import { HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import Link from '~components/Link'
import cloudinaryTransform from '~helpers/cloudinary'

export const CaseStudyHeroLogo = ({ title, src, ...rest }) => {
  const theme = useTheme()

  return (
    <div css={{ marginBottom: theme.space.large }} {...rest}>
      <Image
        css={{
          maxHeight: 100,
          maxWidth: 250,
          filter: 'brightness(0) invert(1)',
        }}
        src={src}
        alt={title}
      />
    </div>
  )
}

CaseStudyHeroLogo.propTypes = {
  title: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
}

export const CaseStudyVideo = ({ teaserImage, url, ...rest }) => {
  const theme = useTheme()

  const [isVideoModalOpen, setVideoModalOpen] = useState(false)

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <Fragment>
      <div
        tabIndex="-1"
        role="button"
        onClick={(e) => {
          // Used to help prevent bubbling the click to the parent link in the teaser view
          e.stopPropagation()

          setVideoModalOpen(true)
        }}
        css={{
          position: 'relative',
          cursor: 'pointer',
          boxShadow: theme.boxShadow.large,

          '&:hover, &:focus': {
            '.CaseStudy__PlayButton': {
              transform: 'translate(-50%, -50%) scale(1.125)',
            },
          },

          [theme.mq.medium]: {
            transform: `translateX(${theme.space.large}px)`,
          },
        }}
        {...rest}
      >
        <div
          className="CaseStudy__VideoTeaser"
          css={{
            paddingBottom: '56.25%',
            borderRadius: theme.borderRadius.large,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Image
            src={teaserImage}
            css={{
              position: 'absolute',
              zIndex: 0,
              top: 0,
              left: 0,
              objectFit: 'cover',
            }}
          />
        </div>
        <div
          className="CaseStudy__PlayButton"
          css={{
            ...gradient.greenTeal(theme),
            width: theme.height.large,
            height: theme.height.large,
            borderRadius: theme.borderRadius.rounded,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
            transformOrigin: 'center center',

            [theme.mq.medium]: {
              left: 0,
            },
          }}
        >
          <Icon
            icon="play"
            css={{
              fill: theme.contrast.base,
              fontSize: theme.fontSize.xlarge,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>
      </div>
      <VideoModal
        open={isVideoModalOpen}
        setIsOpen={setVideoModalOpen}
        title="Case Study video"
        src={url}
      />
    </Fragment>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events */
}

CaseStudyVideo.propTypes = {
  teaserImage: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
}

export const CaseStudyTeaserResults = ({ results }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        // Promote above left-column for cleaner clickability
        position: 'relative',
        zIndex: 2,

        ...gradient.tealGreen(theme),
        borderRadius: theme.borderRadius.base,
        padding: theme.space.large,
        boxShadow: theme.boxShadow.large,

        [theme.mq.small__max]: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },

        [theme.mq.medium]: {
          paddingTop: theme.space.xlarge,
          paddingBottom: theme.space.xlarge,
          paddingLeft: theme.space.large + theme.space.base,
          paddingRight: theme.space.large + theme.space.base,
          transform: `translate(${theme.space.large}px, ${theme.space.large}px)`,
          alignSelf: 'end',
          gridColumn: '8 / span 5',
          gridRow: 1,
        },
      }}
    >
      <List space="large">
        {results.map((result) => (
          <ListItem
            key={result.heading}
            css={[
              text.heading(theme),
              {
                textAlign: 'center',

                [theme.mq.medium]: {
                  textAlign: 'left',
                },
              },
            ]}
          >
            <div css={[theme.fontSize.h3__fluid]}>
              <Data>{result.heading}</Data>
            </div>
            <div
              css={[
                theme.fontSize.xlarge__fluid,
                {
                  fontWeight: theme.fontWeight.base,
                },
              ]}
            >
              <Data>{result.blurb}</Data>
            </div>
          </ListItem>
        ))}
      </List>
    </div>
  )
}

CaseStudyTeaserResults.propTypes = {
  results: PropTypes.array.isRequired,
}

export const CaseStudyTeaserVideo = ({ video: { url, teaserImage } }) => {
  const theme = useTheme()

  return (
    <CaseStudyVideo
      url={url}
      teaserImage={teaserImage}
      css={{
        // Promote above left-column for cleaner clickability
        position: 'relative',
        zIndex: 2,

        [theme.mq.small__max]: {
          '.CaseStudy__VideoTeaser': {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        },

        [theme.mq.medium]: {
          transform: `translateX(${theme.space.large}px)`,
          gridColumn: '6 / span 7',
          gridRow: 1,
        },
      }}
    />
  )
}

CaseStudyTeaserVideo.propTypes = {
  video: PropTypes.object.isRequired,
}

export const CaseStudyTeaser = ({ node }) => {
  const theme = useTheme()

  const ref = useRef()
  const visible = useRef(false)

  const controls = useAnimation()

  const variants = {
    hidden: {
      opacity: 0,
      y: theme.space.xlarge,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const intersection = useIntersection(ref, {
    rootMargin: '0px',
    threshold: 0.15,
  })

  useEffect(() => {
    if (intersection?.isIntersecting && !visible.current) {
      visible.current = true

      controls.start('visible')
    }
  }, [intersection, controls])

  // Use teaser logo if available
  const logo = node.frontmatter.hero.teaserLogo || node.frontmatter.hero.logo

  return (
    <motion.div
      className="u-contrast"
      key={node.fileAbsolutePath}
      ref={ref}
      initial="hidden"
      variants={variants}
      animate={controls}
      css={[
        gradient.dark(theme),
        {
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          borderRadius: theme.borderRadius.base,
          position: 'relative',

          [theme.mq.small__max]: {
            borderBottomLeftRadius: theme.borderRadius.large,
            borderBottomRightRadius: theme.borderRadius.large,
          },
        },

        node.frontmatter.hero.background && {
          '&::after': {
            content: "''",
            position: 'absolute',
            zIndex: 0,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: rgba(theme.color.dark.base, 0.75),
            borderRadius: theme.borderRadius.base,
          },
        },
      ]}
      style={{
        backgroundImage: node.frontmatter.hero.background
          ? `url(${cloudinaryTransform(node.frontmatter.hero.background)})`
          : null,
      }}
    >
      <div
        css={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          alignItems: 'center',

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          },
        }}
      >
        <div
          css={{
            padding: theme.space.large,
            position: 'relative',

            '&:hover, &:focus': {
              '.Customers__FeaturedTeaser__Icon': {
                transform: `translateX(${theme.space.xsmall}px)`,
              },
            },

            [theme.mq.medium]: {
              padding: theme.space.xlarge,
              gridColumn: node.frontmatter.video?.url
                ? '1 / span 6'
                : '1 / span 8',
              gridRow: 1,
            },
          }}
        >
          <Link
            css={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 2,
            }}
            title={node.frontmatter.hero.heading}
            to={node.fields.fullUrl}
          />
          <CaseStudyHeroLogo
            src={logo}
            title={node.frontmatter.title}
            css={{
              img: {
                maxHeight: 60,
              },
            }}
          />
          <HeroTitle as="h2" css={{ marginTop: 0 }}>
            <Data>{node.frontmatter.hero.heading}</Data>
          </HeroTitle>
          <HeroTitleSub>
            <Data>{node.frontmatter.hero.blurb}</Data>
          </HeroTitleSub>
          <div
            css={[
              text.heading(theme),
              {
                marginTop: theme.space.large,
                color: theme.color.primary.base,
              },
            ]}
          >
            Learn more
            <Icon
              className="Customers__FeaturedTeaser__Icon"
              icon="caret-right"
              css={{
                marginLeft: theme.space.small,
                transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
              }}
            />
          </div>
        </div>

        {node.frontmatter.video?.url ? (
          <CaseStudyTeaserVideo video={node.frontmatter.video} />
        ) : (
          <CaseStudyTeaserResults results={node.frontmatter.copy.results} />
        )}
      </div>
    </motion.div>
  )
}

CaseStudyTeaser.propTypes = {
  node: PropTypes.object.isRequired,
}

export const CaseStudyTeasers = ({ caseStudies }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.xlarge,
        position: 'relative',

        [theme.mq.medium]: {
          '&::before': {
            content: "''",
            width: 6,
            height: '100%',
            position: 'absolute',
            top: 0,
            left: theme.space.xlarge,
            zIndex: -1,
            background: generateGradient({
              start: theme.color.primary.base,
              stop: theme.brand.teal,
              position: 'to bottom',
            }),
          },
        },
      }}
    >
      {caseStudies.map(({ node }) => (
        <CaseStudyTeaser key={node.fileAbsolutePath} node={node} />
      ))}
    </div>
  )
}

CaseStudyTeasers.propTypes = {
  caseStudies: PropTypes.array.isRequired,
}
